import { generateDatasetSchema } from "@/utils/seo-ai";
import { useEffect } from "react";

interface CitableStatsProps {
  className?: string;
  variant?: "compact" | "full";
}

const PLATFORM_STATS = [
  { label: "Profesionales verificados", value: "+1,800", numericValue: 1800 },
  { label: "Ciudades cubiertas", value: "157", numericValue: 157 },
  { label: "Gimnasios listados", value: "+1,200", numericValue: 1200 },
  { label: "Entrenadores personales", value: "+400", numericValue: 400 },
];

export const CitableStats = ({ className = "", variant = "compact" }: CitableStatsProps) => {
  const datasetSchema = generateDatasetSchema({
    name: "Estadísticas Ekselencia 2026",
    description: "Datos verificados del directorio fitness líder de España",
    dateModified: new Date().toISOString().split('T')[0],
    metrics: PLATFORM_STATS.map(s => ({ name: s.label, value: s.numericValue }))
  });

  useEffect(() => {
    // Inject dataset schema for this component
    const existingScript = document.querySelector('script[data-citable-stats]');
    if (existingScript) existingScript.remove();

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-citable-stats", "true");
    script.text = JSON.stringify(datasetSchema);
    document.head.appendChild(script);

    return () => {
      const cleanup = document.querySelector('script[data-citable-stats]');
      if (cleanup) cleanup.remove();
    };
  }, []);

  if (variant === "compact") {
    return (
      <section 
        className={`citable-stats ${className}`}
        itemScope 
        itemType="https://schema.org/Dataset"
        aria-label="Estadísticas de la plataforma Ekselencia"
      >
        <meta itemProp="name" content="Estadísticas Ekselencia 2026" />
        <meta itemProp="description" content="Datos verificados del directorio fitness líder de España" />
        <div className="flex flex-wrap justify-center gap-6 md:gap-12 py-4">
          {PLATFORM_STATS.map((stat, index) => (
            <div key={index} className="text-center">
              <span className="block text-2xl md:text-3xl font-bold text-primary" itemProp="variableMeasured">
                {stat.value}
              </span>
              <span className="text-sm text-muted-foreground">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section 
      className={`citable-stats bg-muted/30 rounded-xl p-8 ${className}`}
      itemScope 
      itemType="https://schema.org/Dataset"
      aria-label="Estadísticas completas de Ekselencia"
    >
      <meta itemProp="name" content="Estadísticas Ekselencia 2026" />
      <meta itemProp="description" content="Datos verificados del directorio fitness líder de España" />
      
      <h3 className="text-xl font-bold text-center mb-6">
        Ekselencia en Números
      </h3>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {PLATFORM_STATS.map((stat, index) => (
          <div 
            key={index} 
            className="text-center p-4 bg-background rounded-lg border"
          >
            <span 
              className="block text-3xl md:text-4xl font-bold text-primary mb-2" 
              itemProp="variableMeasured"
            >
              {stat.value}
            </span>
            <span className="text-sm text-muted-foreground">{stat.label}</span>
          </div>
        ))}
      </div>
      
      <p className="text-center text-xs text-muted-foreground mt-6">
        Última actualización: {new Date().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
      </p>
    </section>
  );
};

export default CitableStats;
